import * as React from "react";
import { Box, Card, Chip, CircularProgress, Grid, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { MaskHappyIcon } from "@phosphor-icons/react/dist/ssr/MaskHappy";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { ToggleLeftIcon } from "@phosphor-icons/react/dist/ssr/ToggleLeft";
import { ToggleRightIcon } from "@phosphor-icons/react/dist/ssr/ToggleRight";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { UserListIcon } from "@phosphor-icons/react/dist/ssr/UserList";
import { UsersIcon } from "@phosphor-icons/react/dist/ssr/Users";
import { useNavigate } from "react-router-dom";

import {
	Account,
	accountsKeys,
	useAccountsInfo,
	useActivateAccount,
	useDeactivateAccount,
	useDeleteAccount,
	useImpersonateAccount,
} from "@/hooks/api/use-accounts";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { ConfirmModal } from "@/components/confirm-modal/confirm-modal";
import { useAuth } from "@/components/core/auth-provider";
import { toast } from "@/components/core/toaster";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { StatisticCard } from "@/components/statisticCard/statistic-card";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { CreateAccount } from "./addAccount/index";

export function Accounts(): React.JSX.Element {
	const {
		items: accounts,
		isLoading,
		totalItems,
		refetchData: fetchAccounts,
	} = useCachedTableStateWithUrl<Account>({
		queryKey: accountsKeys.lists(),
		endpoint: "/accounts",
	});

	const { account, setAccount, getMe } = useAuth();
	const navigate = useNavigate();

	const [isCreateDrawerOpen, setIsCreateDrawerOpen] = React.useState(false);
	const [selectedAccount, setSelectedAccount] = React.useState<Account | null>(null);
	const [impersonateId, setImpersonateId] = React.useState<string | null>(null);

	const { data: accountsInfo, isLoading: isAccountsInfoLoading } = useAccountsInfo();
	const deleteAccountMutation = useDeleteAccount();
	const impersonateAccountMutation = useImpersonateAccount();
	const deactivateAccountMutation = useDeactivateAccount();
	const activateAccountMutation = useActivateAccount();

	const {
		open: isDeleteAccountModalOpen,
		data: selectedAccountToDelete,
		handleClose: handleDeleteAccountModalClose,
		handleOpen: handleDeleteAccountModalOpen,
	} = useDialog<Account | null>();

	const {
		open: isActivateAccountModalOpen,
		data: selectedAccountToActivate,
		handleClose: handleActivateAccountModalClose,
		handleOpen: handleActivateAccountModalOpen,
	} = useDialog<Account | null>();

	const handleDeleteAccount = async () => {
		if (selectedAccountToDelete?.id) {
			await deleteAccountMutation.mutateAsync(selectedAccountToDelete.id);
			handleDeleteAccountModalClose();
		}
	};

	const handleImpersonate = async (account: Account) => {
		try {
			setImpersonateId(account.id);
			const response = await impersonateAccountMutation.mutateAsync(account.id);
			setAccount(response.account);
			await getMe();
			toast.success("Account impersonated successfully!");
		} finally {
			setImpersonateId(null);
		}
	};

	const handleManageAccountUsers = async (account: Account) => {
		navigate(`/accounts/${account.id}/users`);
	};

	const handleDeactivateAccount = async () => {
		const accountId = selectedAccountToDelete?.id || selectedAccountToActivate?.id;

		if (!accountId) {
			return;
		}

		await deactivateAccountMutation.mutateAsync(accountId);
		handleDeleteAccountModalClose();
	};

	const handleActivateAccount = async () => {
		const accountId = selectedAccountToActivate?.id;

		if (!accountId) {
			return;
		}

		await activateAccountMutation.mutateAsync(accountId);
		handleActivateAccountModalClose();
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "name",
					label: "Name",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "name",
			},
			{
				header: {
					id: "enabled",
					label: "Enabled",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "enabled",
				getCellValue: (row: any) => {
					const isEnabled = row.enabled;

					if (!isEnabled) {
						return null;
					}

					return (
						<Chip
							// icon={<CheckCircleIcon color="var(--mui-palette-success-main)" weight="fill" />}
							label={"Enabled"}
							size="small"
							variant="filled"
							color="success"
						/>
					);
				},
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Manage Users",
									icon: <UserListIcon width={20} height={20} weight="fill" color="var(--mui-palette-info-main)" />,
									onClick: () => handleManageAccountUsers(row),
								},
								{
									label: "Impersonate",
									icon:
										impersonateId === row.id ? (
											<CircularProgress size={20} />
										) : (
											<MaskHappyIcon width={20} height={20} weight="bold" color="var(--mui-palette-primary-main)" />
										),
									sx: {
										color: "var(--mui-palette-primary-main)",
										position: "relative",

										"&::before": account?.id
											? {}
											: {
													content: '""',
													position: "absolute",
													top: "6px",
													left: "6px",
													right: "6px",
													bottom: "6px",
													borderRadius: "50%",
													border: "2px solid var(--mui-palette-primary-main)",
													animation: "ripple 2s ease-in-out infinite",
													opacity: 0.6,
												},
										"@keyframes ripple": {
											"0%": {
												transform: "scale(1)",
												opacity: 0.6,
											},
											"50%": {
												transform: "scale(1.3)",
												opacity: 0.2,
											},
											"100%": {
												transform: "scale(1.6)",
												opacity: 0,
											},
										},
									},
									onClick: () => (impersonateId === row.id ? null : handleImpersonate(row)),
									hidden: account?.id === row.id,
								},
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setIsCreateDrawerOpen(true);
										setSelectedAccount(row);
									},
								},
								{
									label: row.enabled ? "Deactivate" : "Activate",
									icon: row.enabled ? (
										<ToggleRightIcon width={32} height={32} weight="fill" />
									) : (
										<ToggleLeftIcon width={32} height={32} weight="fill" />
									),
									sx: { color: row.enabled ? "success.main" : "default" },
									onClick: () => {
										handleActivateAccountModalOpen(row);
									},
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteAccountModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, [account, impersonateId]);

	const statisticCards = React.useMemo(() => {
		return [
			{
				title: "Total Accounts",
				value: accountsInfo?.totalAccounts || 0,
				icon: <UsersIcon color="var(--mui-palette-primary-main)" width={20} height={20} />,
			},
			{
				title: "Active Accounts",
				value: accountsInfo?.activeAccounts || 0,
				icon: <UsersIcon color="var(--mui-palette-success-main)" width={20} height={20} />,
			},
			// {
			// 	title: "Active Accounts",
			// 	value: 280,
			// 	icon: <UsersIcon color="var(--mui-palette-success-main)" width={20} height={20} />,
			// 	additonalInfo: "88% active",
			// },
			// {
			// 	title: "Active Accounts",
			// 	value: 80,
			// 	icon: <UsersIcon color="var(--mui-palette-success-main)" width={20} height={20} />,
			// 	additonalInfo: "88% this month",
			// },
		];
	}, [accountsInfo]);

	const animationDelay = 300;

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Accounts Management</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Manage client accounts and their details
							</Typography>
						</Box>
						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Button
								startIcon={<PlusIcon />}
								variant="contained"
								onClick={() => {
									setIsCreateDrawerOpen(true);
									setSelectedAccount(null);
								}}
							>
								Add Account
							</Button>
						</Box>
					</Stack>

					<Grid container spacing={3}>
						{statisticCards.map((card, index) => (
							<Grid
								key={index}
								size={{
									md: 3,
									sm: 6,
									xs: 12,
								}}
							>
								<StatisticCard
									{...card}
									isLoading={isAccountsInfoLoading}
									animationDelay={index * animationDelay}
									timeout={animationDelay * statisticCards.length}
									isLast={index === statisticCards.length - 1}
								/>
							</Grid>
						))}
					</Grid>

					<TableAnimation delay={450}>
						<Card>
							<CustomTable
								columns={columns}
								data={accounts}
								refetchData={fetchAccounts}
								isLoading={isLoading}
								totalItems={totalItems}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeleteAccountModalOpen}
				onClose={handleDeleteAccountModalClose}
				onDelete={handleDeleteAccount}
				onAdditionalAction={selectedAccountToDelete?.enabled ? handleDeactivateAccount : undefined}
				title="Confirm account deletion?"
				description="<b>Deletion is permanent</b> (data loss)."
				warningText={
					selectedAccountToDelete?.enabled ? "This action cannot be undone. Choose <b>Deactivate</b> to hide the account temporarily." : undefined
				}
			/>

			<ConfirmModal
				open={isActivateAccountModalOpen}
				onClose={handleActivateAccountModalClose}
				onSumbit={selectedAccountToActivate?.enabled ? handleDeactivateAccount : handleActivateAccount}
				title={selectedAccountToActivate?.enabled ? "Confirm account deactivation?" : "Confirm account activation?"}
				description={
					selectedAccountToActivate?.enabled
						? "Deactivation will hide the account from users."
						: "Activation will make the account visible to users."
				}
			/>

			<CreateAccount
				account={selectedAccount}
				isCreateDrawerOpen={isCreateDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreateDrawerOpen(false);

					setTimeout(() => {
						setSelectedAccount(null);
					}, 300);
				}}
			/>
		</React.Fragment>
	);
}
